import { gql } from "generated-graphql";

/**
 * The document itself, for the review dialog's title bar and preview pane.
 * Deliberately narrow — `useDocumentMetadata` (the manifest review dialog's
 * hook) also runs a facility-match lookup this surface has no use for, since
 * decision 4 always shows a facility picker rather than suggesting one.
 */
export const SHIPPING_DOCUMENT_FILE = gql(`
  query ShippingDocumentFile($id: ID!) {
    document(id: $id) {
      id
      tenantId
      title
      fileExtension
      downloadLink
      extractionStatus
      extractionError
      sourceWasteManifest {
        id
      }
    }
  }
`);

/**
 * The AI reading of a bill of lading document, for the review grid.
 *
 * No polling here: the review dialog only opens once
 * `DocumentAIAnalyzingDialog` has already reported the enrichment job
 * SUCCEEDED, so by the time this query runs the candidate is already
 * written. `onRetry` (grid README) re-queues via `EXTRACT_BILL_OF_LADING`
 * and the caller re-fetches.
 */
export const EXTRACTED_BILL_OF_LADING = gql(`
  query ExtractedBillOfLading($documentId: ID!) {
    extractedBillOfLading(documentId: $documentId) {
      documentId
      candidateStatus
      error
      values {
        documentNumber
        generatorName
        transporterName
        destinationName
        shipDate
        receivedDate
      }
      lines {
        description
        quantity
        unit
        declaredWasteKind
        federalWasteCodes
        stateWasteCodes
      }
      # What the model itself read, before any correction a reviewer saved. The
      # grid shows it as "Suggested: x" beside a field that no longer matches
      # and offers it back on click. Without it the grid's extracted baseline
      # falls back to values, which the resolver has already run corrections
      # over, so a second review pass shows a reviewer their own earlier edit
      # as the extraction and the model's reading is gone from the screen.
      asExtractedValues {
        documentNumber
        generatorName
        transporterName
        destinationName
        shipDate
        receivedDate
      }
      asExtractedLines {
        description
        quantity
        unit
        declaredWasteKind
        federalWasteCodes
        stateWasteCodes
      }
      fieldConfidences {
        fieldPath
        confidence
      }
      # Set once the bill has a saved manifest; values and lines then come from it.
      savedWasteManifestId
    }
  }
`);

/**
 * Queue Document-AI extraction of an uploaded bill of lading. Returns the
 * enrichment job id, which `DocumentAIAnalyzingDialog` polls the same way the
 * manifest upload flow does — this extractor rides the same job queue.
 */
export const EXTRACT_BILL_OF_LADING = gql(`
  mutation ExtractBillOfLading($documentId: ID!) {
    extractBillOfLading(documentId: $documentId)
  }
`);

/**
 * Record the reviewer's corrections against the candidate and write the
 * reviewed values as a bill of lading manifest. Returns the manifest id, or the
 * reasons nothing was written. The reading is refetched alongside: an applied
 * candidate flips to APPLIED, and leaving the old reading cached would let a
 * second open of the dialog offer to re-apply it.
 */
export const APPLY_EXTRACTED_BILL_OF_LADING = gql(`
  mutation ApplyExtractedBillOfLading($input: ApplyExtractedBillOfLadingInput!) {
    applyExtractedBillOfLading(input: $input) {
      wasteManifestId
      blockers
    }
  }
`);
/**
 * Whether an uploaded document has reached its Shipment Documents row yet.
 * `sourceWasteManifest` is null when the reading wrote nothing, and
 * `processedManifestId` is null until process-manifests has run.
 */
export const BILL_OF_LADING_ROW_STATUS = gql(`
  query BillOfLadingRowStatus($id: ID!) {
    document(id: $id) {
      id
      sourceWasteManifest {
        id
        processedManifestId
      }
    }
  }
`);
